/**
 * CreateTestWizard -- 7-Step Test Creation Wizard
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 * Route: /test-admin/tests/create
 *
 * After launch, redirects to monitoring dashboard.
 */

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import { WIZARD_STEPS } from "../../types";
import { WizardStepper } from "../../components/WizardStepper";

import { ScenarioSelectionStep } from "./ScenarioSelectionStep";
import { TestDetailsStep } from "./TestDetailsStep";
import { ParticipantsStep } from "./ParticipantsStep";
import { ConfigurationStep } from "./ConfigurationStep";
import { ReviewLaunchStep } from "./ReviewLaunchStep";

type WizardData = Record<string, unknown>;

export function CreateTestWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({});

  const handleNext = useCallback(
    (stepData?: WizardData) => {
      if (stepData) {
        setWizardData((prev) => ({ ...prev, ...stepData }));
      }
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
    },
    [currentStep]
  );

  const handleBack = useCallback((stepData?: WizardData) => {
    if (stepData) {
      setWizardData(prev => ({ ...prev, ...stepData }));
    }
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleLaunch = useCallback(async () => {
    try {
      // Strip display-only fields before sending to API. Config fields
      // (back_navigation_enabled, min_option_points_threshold) are emitted
      // by ConfigurationStep in snake_case already, so they flow through
      // apiPayload as-is — no camelCase remapping here, or it would
      // override the real values.
      const { scenario_name, scenario_description, version_name, ...apiPayload } = wizardData;
      const test = await apiClient.post<{
        id: string;
        skipped_role_conflicts?: string[];
      }>("/api/tests", {
        ...apiPayload,
        scenario_type: apiPayload.scenario,
      });
      if (test.skipped_role_conflicts?.length) {
        showToast({
          variant: "warning",
          title: "Some participants were skipped",
          description: test.skipped_role_conflicts
            .map((e) => `${e} already has admin roles and cannot be added as a candidate`)
            .join(". "),
        });
      }
      navigate(`/test-admin/tests/${test.id}/monitor`);
    } catch {
      navigate("/test-admin/tests");
    }
  }, [wizardData, navigate]);

  const stepComponents = [
    ScenarioSelectionStep,
    TestDetailsStep,
    ParticipantsStep,
    ConfigurationStep,
    ReviewLaunchStep,
  ];

  const StepComponent = stepComponents[currentStep];
  if (!StepComponent) return null;

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <main className="px-6 py-10 w-full max-w-5xl mx-auto">
      <WizardStepper steps={WIZARD_STEPS} currentStep={currentStep} />
      <div className="mt-6">
        <StepComponent wizardData={wizardData} onNext={isLastStep ? handleLaunch : handleNext} onBack={handleBack} />
      </div>
    </main>
  );
}

export default CreateTestWizard;
